import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import DigestTicker from '../components/DigestTicker'
import { useCmsLanding } from '../hooks/useCmsLanding'
import { DEFAULT_LANDING } from '../config/landingDefaults'
import { RsIcon } from '../components/icons'

export default function Landing() {
  const { cms, loading } = useCmsLanding()
  const featureTabs = cms?.featureTabs || []
  const [activeFeature, setActiveFeature] = useState(null)
  const [showContactQr, setShowContactQr] = useState(false)

  useEffect(() => {
    if (featureTabs.length && !activeFeature) {
      setActiveFeature(featureTabs[0].id)
    }
  }, [featureTabs, activeFeature])

  const feature = featureTabs.find((f) => f.id === activeFeature) || featureTabs[0] || null
  const hero = DEFAULT_LANDING.hero
  const statItems = DEFAULT_LANDING.stats.items
  const brandName = cms?.nav?.brandName || 'RotationStreet'
  const footerCopy = cms?.footer?.copyright || '© 2026 Rotation Street. All rights reserved.'
  const howItWorks = cms?.howItWorks || { title: '', steps: [] }
  const cta = cms?.cta || {}
  const heroTitleLine2 = hero.titleLine2 || hero.titleHighlight || ''

  if (loading && !cms) {
    return <div className="landing-page" style={{ padding: 48, textAlign: 'center' }}>Loading…</div>
  }

  return (
    <div className="landing-page">
      <nav className="landing-navbar">
        <div className="landing-nav-left">
          <Link to="/" className="landing-nav-logo">
            {brandName.includes('Street') ? (
              <>Rotation<span>Street</span></>
            ) : (
              brandName
            )}
          </Link>
        </div>
        <div className="landing-nav-right">
          <Link to="/login" className="landing-btn landing-btn-outline landing-btn-sm">
            {cms?.nav?.loginLabel || 'Log In'}
          </Link>
          <Link to="/signup" className="landing-btn landing-btn-primary landing-btn-sm">
            {cms?.nav?.ctaLabel || 'Get Started'}
          </Link>
        </div>
      </nav>

      <DigestTicker />

      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-badge">
            <RsIcon name="clock" size="sm" />
            {hero.badge}
          </div>
          <h1>
            {hero.titleLine1}
            {heroTitleLine2 && (
              <>
                <br />
                <span className="highlight">{heroTitleLine2}</span>
              </>
            )}
          </h1>
          <p className="landing-hero-desc">{hero.subtitle}</p>
          <div className="landing-hero-actions">
            <Link to="/signup?role=Startup" className="landing-btn landing-btn-primary" style={{ padding: '13px 32px', fontSize: 15 }}>
              {hero.gpCta}
            </Link>
            <Link to="/signup?role=LP" className="landing-btn landing-btn-accent" style={{ padding: '13px 32px', fontSize: 15 }}>
              {hero.lpCta}
            </Link>
          </div>
          <div className="landing-hero-stats">
            {statItems.map((item) => (
              <div key={item.label}>
                <div className="landing-stat-num">{item.value}</div>
                <div className="landing-stat-label">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-section-label">Platform Features</div>
        <h2>{cms?.featuresSection?.title}</h2>
        <p className="landing-section-desc">{cms?.featuresSection?.description}</p>
        {featureTabs.length > 0 && feature && (
          <div className="landing-features-layout">
            <div className="landing-features-sidebar">
              {featureTabs.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`landing-sidebar-item${activeFeature === f.id ? ' active' : ''}`}
                  onClick={() => setActiveFeature(f.id)}
                >
                  <span className="rs-icon-wrap">
                    <RsIcon name={f.icon || 'deal'} size="lg" />
                  </span>
                  <div>
                    <div className="landing-sidebar-item-title">{f.title}</div>
                    <div className="landing-sidebar-item-sub">{f.sub}</div>
                  </div>
                </button>
              ))}
            </div>
            <div className="landing-features-content">
              <div className="landing-feature-detail">
                <h3>{feature.heading}</h3>
                <p className="sub">{feature.subheading}</p>
                <p>{feature.body}</p>
                <div className="landing-feature-tags">
                  {(feature.tags || []).map((tag) => (
                    <span key={tag} className="landing-feature-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="landing-hiw">
        <div className="landing-hiw-inner">
          <div className="landing-section-label">How It Works</div>
          <h2>{howItWorks.title}</h2>
          <div className="landing-hiw-steps">
            {(howItWorks.steps || []).map((step, i) => (
              <div key={step.title} className="landing-hiw-step">
                <div className="landing-hiw-num">{i + 1}</div>
                <h4>{step.title}</h4>
                <p>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta">
        <h2>{cta.title}</h2>
        <p>{cta.description}</p>
        <Link to="/signup" className="landing-btn landing-btn-accent" style={{ fontSize: 16, padding: '14px 36px' }}>
          {cta.buttonLabel}
        </Link>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-col">
            <h4>RotationStreet</h4>
            <p style={{ fontSize: 13, lineHeight: 1.8 }}>{cms?.footer?.tagline}</p>
          </div>
          <div className="landing-footer-col">
            <h4>Product</h4>
            <ul>
              <li>
                <Link to="/signup?role=GP">For GP</Link>
              </li>
              <li>
                <Link to="/signup?role=LP">For LP</Link>
              </li>
            </ul>
          </div>
          <div className="landing-footer-col">
            <h4>Company</h4>
            <ul>
              <li>
                <a href="mailto:careers@rotationstreet.com">Careers</a>
              </li>
              <li>
                <button type="button" className="landing-footer-link" onClick={() => setShowContactQr(true)}>
                  Contact
                </button>
              </li>
            </ul>
          </div>
          <div className="landing-footer-col">
            <h4>Legal</h4>
            <ul>
              <li>Privacy Policy</li>
              <li>Terms of Service</li>
              <li>
                <button type="button" className="landing-footer-link" onClick={() => setShowContactQr(true)}>
                  Compliance
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="landing-footer-bottom">
          <span>{footerCopy}</span>
          <span>{cms?.footer?.madeWith}</span>
        </div>
      </footer>

      {showContactQr && (
        <div className="landing-qr-overlay" onClick={() => setShowContactQr(false)} role="presentation">
          <div className="landing-qr-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Contact WeChat QR code">
            <button type="button" className="landing-qr-close" onClick={() => setShowContactQr(false)} aria-label="Close">
              ×
            </button>
            <img src="/images/contact-wechat-qr.png" alt="RotationStreet WeChat QR code" className="landing-qr-image" />
            <p className="landing-qr-caption">Scan QR code to add RotationStreet on WeChat</p>
          </div>
        </div>
      )}
    </div>
  )
}
